/**
 * @component DismissChangesDialog
 * @description
 * A confirmation dialog shown when the user attempts to leave a settings page
 * with unsaved changes. It follows the SIB modal design system and uses
 * Chakra UI v3 Dialog components.
 *
 * ---
 * **Features**
 * - Displays a confirmation prompt following the approved UI mockup.
 * - “Verwerfen” closes the dialog without performing any action.
 * - “Speichern” calls the `onDismiss` callback (e.g. save or discard logic), then closes the dialog.
 * - Supports closing via the dialog close button (X) or clicking outside.
 *
 * ---
 * **Props**
 * @prop {boolean} isOpen
 * Controls whether the dialog is currently visible.
 *
 * @prop {() => void} onClose
 * Callback fired when the dialog should close.
 * Triggered by: Cancel button, close icon, clicking outside, or programmatic close.
 *
 * @prop {() => void} onDismiss
 * Callback fired when the user confirms the action.
 * After calling `onDismiss()`, the dialog automatically closes via `onClose()`.
 *
 * ---
 * @example
 * ```tsx
 * const [open, setOpen] = useState(false);
 *
 * <DismissChangesDialog
 *   isOpen={open}
 *   onClose={() => setOpen(false)}
 *   onDismiss={() => console.log("Changes dismissed")}
 * />
 * ```
 */

import { Dialog, Button, Text, CloseButton, HStack, Portal } from '@chakra-ui/react'
import type { ReactNode } from 'react'

interface DismissChangesDialogProps {
  isOpen: boolean
  onClose: () => void
  onDismiss: () => void
  onSave: () => void
  onNavigate: () => void
  /** What is unsaved, shown under the question (e.g. a list of the pending changes). */
  details?: ReactNode
}

export function DismissChangesDialog({
  isOpen,
  onClose,
  onDismiss,
  onSave,
  onNavigate,
  details,
}: DismissChangesDialogProps) {
  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={e => {
        if (!e.open) onClose()
      }}
    >
      <Portal>
        {/* Backdrop */}
        <Dialog.Backdrop />

        {/* Content */}
        <Dialog.Positioner>
          <Dialog.Content>
            {/* Header */}
            <Dialog.Header>
              <Dialog.Title>Ungespeicherte Änderungen</Dialog.Title>

              <Dialog.CloseTrigger asChild>
                <CloseButton />
              </Dialog.CloseTrigger>
            </Dialog.Header>

            {/* Body */}
            <Dialog.Body>
              <Text>
                Sie haben Änderungen in Ihren Einstellungen vorgenommen. Möchten Sie diese
                verwerfen oder speichern?
              </Text>
              {details}
            </Dialog.Body>

            {/* Footer */}
            <Dialog.Footer>
              <HStack gap={4} justify="flex-end" w="full">
                <Button
                  variant="outline"
                  onClick={() => {
                    onDismiss()
                    onClose()
                    onNavigate()
                  }}
                >
                  Verwerfen
                </Button>

                <Button
                  variant="subtle"
                  colorPalette="green"
                  onClick={() => {
                    onSave()
                    onClose()
                    onNavigate()
                  }}
                >
                  Speichern
                </Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
